import { apiClient } from './apiClient';

// Types
export interface SystemSetting {
  key: string;
  value: unknown;
  dataType: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON' | 'ARRAY';
  description: string;
}

export interface SettingsGroup {
  category: string;
  settings: SystemSetting[];
}

export interface BusinessHours {
  id: string;
  dayOfWeek: number;
  dayName: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  breakStart: string | null;
  breakEnd: string | null;
}

export interface Holiday {
  id: string;
  name: string;
  date: string;
  isRecurring: boolean;
  isClosed: boolean;
}

export interface SettingHistory {
  id: string;
  settingId: string;
  category: string;
  key: string;
  oldValue: unknown;
  newValue: unknown;
  changedBy: string;
  changedAt: string;
}

export interface CreateHolidayRequest {
  name: string;
  date: string;
  isRecurring?: boolean;
  isClosed?: boolean;
}

export interface UpdateBusinessHoursRequest {
  isOpen: boolean;
  openTime?: string;
  closeTime?: string;
  breakStart?: string | null;
  breakEnd?: string | null;
}

// API response types
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export const settingsApi = {
  // ============ Settings ============

  /**
   * Get all system settings grouped by category
   */
  async getAll(): Promise<SettingsGroup[]> {
    const response = await apiClient.get<ApiResponse<SettingsGroup[]>>(
      '/api/admin/settings'
    );
    return response.data.data;
  },

  /**
   * Update a single setting
   */
  async updateSetting(category: string, key: string, value: unknown): Promise<void> {
    await apiClient.patch(`/api/admin/settings/${category}/${key}`, { value });
  },

  // ============ Business Hours ============

  /**
   * Get business hours for all days of the week
   */
  async getBusinessHours(): Promise<BusinessHours[]> {
    const response = await apiClient.get<ApiResponse<BusinessHours[]>>(
      '/api/admin/settings/business-hours'
    );
    return response.data.data;
  },

  /**
   * Update business hours for a specific day
   */
  async updateBusinessHours(day: number, data: UpdateBusinessHoursRequest): Promise<void> {
    await apiClient.patch(`/api/admin/settings/business-hours/${day}`, data);
  },

  // ============ Holidays ============

  /**
   * Get all holidays
   */
  async getHolidays(): Promise<Holiday[]> {
    const response = await apiClient.get<ApiResponse<Holiday[]>>(
      '/api/admin/settings/holidays'
    );
    return response.data.data;
  },

  /**
   * Create a new holiday
   */
  async createHoliday(data: CreateHolidayRequest): Promise<Holiday> {
    const response = await apiClient.post<ApiResponse<Holiday>>(
      '/api/admin/settings/holidays',
      data
    );
    return response.data.data;
  },

  /**
   * Delete a holiday
   */
  async deleteHoliday(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/settings/holidays/${id}`);
  },

  // ============ History ============

  /**
   * Get settings change history
   */
  async getHistory(limit?: number): Promise<SettingHistory[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiClient.get<ApiResponse<SettingHistory[]>>(
      `/api/admin/settings/history${params}`
    );
    return response.data.data;
  },
};
